import { useCallback, useEffect, useRef, useState } from "react";
import {
  clampDock,
  clearDockPosition,
  movedEnoughToDrag,
  readDockPosition,
  writeDockPosition,
  type DockPoint,
} from "./dockPosition";

/**
 * Drag the assistant anywhere on screen.
 *
 * The first drag freezes wherever the placement classes had already put it --
 * read straight off the element's own rect -- and then applies pointer deltas.
 * Nothing here needs to know how tall the bubble or an open popover above the
 * avatar are, which is what keeps it correct for all four corners.
 *
 * Pointer events (not mouse) so a touchscreen drags too, and pointer capture
 * so letting go outside the window still ends the drag rather than leaving the
 * assistant glued to the cursor.
 */
export function useDockDrag(
  dockRef: React.RefObject<HTMLElement | null>,
  /** The corner chosen in Settings. Changing it discards a drag, so that
   *  setting stays the way to put the assistant back. */
  placement?: string,
) {
  const [at, setAt] = useState<DockPoint | null>(() => readDockPosition(placement));

  // Re-read when the chosen corner changes: the saved position is stamped with
  // the corner it was made under, so this resolves to null and hands control
  // back to the setting. Covers a live change and a reload alike.
  useEffect(() => {
    setAt(readDockPosition(placement));
  }, [placement]);
  const drag = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    moved: boolean;
    captured: boolean;
  } | null>(null);
  /** Set for the duration of the click that ends a drag, so the avatar's own
   *  onClick does not also fire and open the popover on let-go. */
  const suppressClick = useRef(false);
  /** Read inside a state updater, where a captured prop would be stale. */
  const placementRef = useRef(placement);
  placementRef.current = placement;

  /** Pull the dock back inside the window at whatever size it is now. */
  const reclamp = useCallback(() => {
    const el = dockRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setAt((current) =>
      current
        ? clampDock(
            current,
            { width: rect.width, height: rect.height },
            { width: window.innerWidth, height: window.innerHeight },
          )
        : current,
    );
  }, [dockRef]);

  // Re-clamped whenever the dock CHANGES SIZE, not only when the window does.
  //
  // A position is clamped against the dock as it stands, and while the
  // assistant is collapsed that is a small avatar. Opening the popover makes
  // it several times wider — so a dock dragged near the right-hand edge was
  // perfectly in bounds until it was opened, and then the panel hung off the
  // side of the screen with its close button beyond the edge.
  //
  // The same applies to the offer bubble appearing and to the popover closing
  // again, which is why this watches the element rather than any one of them.
  useEffect(() => {
    if (!at) return;
    const el = dockRef.current;
    window.addEventListener("resize", reclamp);
    const observer =
      typeof ResizeObserver === "function" && el ? new ResizeObserver(reclamp) : null;
    observer?.observe(el as Element);
    return () => {
      window.removeEventListener("resize", reclamp);
      observer?.disconnect();
    };
    // `at` only gates whether this runs at all; re-clamping reads the live
    // element, so it must not re-subscribe on every pixel of a drag.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [at === null, dockRef, reclamp]);

  const onPointerDown = useCallback(
    (event: React.PointerEvent) => {
      // Exclude a secondary or middle button; a right-click is a context menu.
      //
      // Deliberately `> 0` and not `!== 0`: `button` is absent on a synthetic
      // pointer event in any environment without a real PointerEvent (jsdom,
      // some automation), and `undefined !== 0` would silently refuse every
      // drag there. What we mean is "not a secondary button", not "exactly
      // the primary one".
      if (event.button > 0) return;
      const el = dockRef.current;
      if (!el) return;
      // A drag that ended without the browser delivering a click (released
      // off-window, or a touch that never synthesises one) would otherwise
      // leave the flag set and eat the NEXT genuine click.
      suppressClick.current = false;
      const rect = el.getBoundingClientRect();
      drag.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: at?.x ?? rect.left,
        originY: at?.y ?? rect.top,
        moved: false,
        captured: false,
      };
      // Capture is taken only once a real drag starts, never on a plain press.
      //
      // A captured pointer retargets its later events to the capturing
      // element — including the implicit click. These handlers live on the
      // dock wrapper while the launcher's own onClick lives on the button
      // inside it, so capturing here meant the click fired on the wrapper and
      // the button never heard it: pressing Mochi mostly did nothing.
    },
    [at, dockRef],
  );

  const onPointerMove = useCallback(
    (event: React.PointerEvent) => {
      const d = drag.current;
      if (!d || d.pointerId !== event.pointerId) return;
      const dx = event.clientX - d.startX;
      const dy = event.clientY - d.startY;
      if (!d.moved && !movedEnoughToDrag(dx, dy)) return;
      if (!d.moved) {
        d.moved = true;
        // Now it is unambiguously a drag, so follow the pointer outside the
        // window — there is no click left to protect.
        d.captured = true;
        (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
      }
      // Stop the press turning into a text selection or a page scroll.
      event.preventDefault();

      const el = dockRef.current;
      const rect = el?.getBoundingClientRect();
      setAt(
        clampDock(
          { x: d.originX + dx, y: d.originY + dy },
          { width: rect?.width ?? 0, height: rect?.height ?? 0 },
          { width: window.innerWidth, height: window.innerHeight },
        ),
      );
    },
    [dockRef],
  );

  const endDrag = useCallback((event: React.PointerEvent) => {
    const d = drag.current;
    if (!d || d.pointerId !== event.pointerId) return;
    drag.current = null;
    if (d.captured) {
      (event.currentTarget as HTMLElement).releasePointerCapture?.(event.pointerId);
    }
    if (!d.moved) return;
    suppressClick.current = true;
    setAt((current) => {
      if (current) writeDockPosition(current, placementRef.current);
      return current;
    });
  }, []);

  /** True when the click that just arrived was the end of a drag. Reading it
   *  consumes it, so the next genuine click still opens the popover. */
  const consumeClickSuppression = useCallback(() => {
    if (!suppressClick.current) return false;
    suppressClick.current = false;
    return true;
  }, []);

  const reset = useCallback(() => {
    clearDockPosition();
    setAt(null);
  }, []);

  return {
    at,
    dragging: drag.current?.moved ?? false,
    reset,
    consumeClickSuppression,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
    },
  };
}
